import { createSlice, createAsyncThunk, PayloadAction } from "@reduxjs/toolkit";
import axios from "axios";
import type { Location } from "./location";
import { RootState } from "../../store";
import { enhancedApiClient, isSuccessResponse, ApiResponse } from "../../services/enhancedApiClient";

interface LocationSlice {
  locations: Location[];
  formData: Partial<Location>;
  loading: boolean;
  error: string | null;
  pagination: {
    page: number;
    limit: number;
    total: number;
    pages: number;
  } | null;
}

const initialState: LocationSlice = {
  locations: [],
  formData: { locationName: "", status: 1 },
  loading: false,
  error: null,
  pagination: null,
};

//get list
export const fetchLocations = createAsyncThunk(
  "location/fetchLocations",
  async (params: { page?: number; limit?: number; search?: string } = {}, { rejectWithValue }) => {
    try {
      const queryParams = new URLSearchParams();
      if (params?.page) queryParams.append('page', params.page.toString());
      if (params?.limit) queryParams.append('limit', params.limit.toString());
      if (params?.search) queryParams.append('search', params.search);
      const url = `/master/location${queryParams.toString() ? `?${queryParams.toString()}` : ''}`;
      const response = await enhancedApiClient.get(url);
      if (isSuccessResponse(response)) {
        console.log("Fetch location response:", response.data);
        return response.data;
      } else {
        return rejectWithValue("Failed to fetch locations");
      }
    } catch (error: any) {
      console.error("Fetch locations error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to fetch locations";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Create State
export const createLocation = createAsyncThunk(
  "location/createLocation",
  async (data: Partial<Location>, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.post("/master/location", data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to create location");
      }
    } catch (error: any) {
      console.error("Create location error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to create color";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Edit location
export const editLocation = createAsyncThunk(
  "location/editLocation",
  async ({ id, data }: { id: number; data: Partial<Location> }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.put(`/master/location/${id}`, data);

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to update location");
      }
    } catch (error: any) {
      console.error("Edit location error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to update location";
      return rejectWithValue(errorMessage);
    }
  }
);

// ✅ Toggle location status
export const toggleLocationStatus = createAsyncThunk(
  "location/toggleLocationStatus",
  async ({ id, status }: { id: number; status: number }, { rejectWithValue }) => {
    try {
      const response = await enhancedApiClient.patch(`/master/location/${id}/status`, { status });

      if (isSuccessResponse(response)) {
        return response.data;
      } else {
        return rejectWithValue("Failed to toggle location status");
      }
    } catch (error: any) {
      console.error("Toggle location status error:", error);
      const errorMessage =
        error.response?.data?.message ||
        error.message ||
        "Failed to toggle location status";
      return rejectWithValue(errorMessage);
    }
  }
);

export const locationSlice = createSlice({
  name: "location",
  initialState,
  reducers: {
    setStateName: (state, action: PayloadAction<string>) => {
      state.formData.locationName = action.payload;
    },
    setLocationStatus: (state, action: PayloadAction<number>) => {
      state.formData.status = action.payload;
    },
    resetForm: (state) => {
      state.formData = { locationName: "", status: 1 };
      state.error = null;
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch
      .addCase(fetchLocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchLocations.fulfilled, (state, action) => {
        state.loading = false;
        state.locations = action.payload.records;
        state.pagination = action.payload.pagination;
        console.log("locations fetched successfully:", action.payload);
      })
      .addCase(fetchLocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Create
      .addCase(createLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(createLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        if(action.payload) {
          const newLocation: Location = {
            id: action.payload.locationId,
            locationId: action.payload.locationId,
            locationName: action.payload.locationName,
            status: action.payload.status,
            updated_at: action.payload.updatedOn,
            updated_by: action.payload.updatedBy,
            action:action.payload.action,
          };
          state.locations.unshift(newLocation);
          }
        state.formData = { locationName: "", status: 1 };
      })
      .addCase(createLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Edit
      .addCase(editLocation.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(editLocation.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedLocation : Location = {
          id: action.payload.locationId,
          locationId: action.payload.locationId,
          locationName: action.payload.locationName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action:action.payload.action,
        }
        const index = state.locations.findIndex(s => s.id === updatedLocation.id);
        if (index !== -1) {
          state.locations[index] = updatedLocation;
        }
        state.formData = { locationName: "", status: 1 };
      })
      .addCase(editLocation.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })

      // Toggle Status
      .addCase(toggleLocationStatus.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(toggleLocationStatus.fulfilled, (state, action) => {
        state.loading = false;
        state.error = null;
        const updatedLocation : Location = {
          id: action.payload.locationId,
          locationId: action.payload.locationId,
          locationName: action.payload.locationName,
          status: action.payload.status,
          updated_at: action.payload.updatedOn,
          updated_by: action.payload.updatedBy,
          action: action.payload.action,
        }
        const index = state.locations.findIndex(s => s.locationId === updatedLocation.locationId);
        if (index !== -1) {
          state.locations = [
            ...state.locations.slice(0, index),
            updatedLocation,
            ...state.locations.slice(index + 1)
          ];
        }
      })
      .addCase(toggleLocationStatus.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { setStateName, setLocationStatus, resetForm } = locationSlice.actions;
// ✅ Selectors
export const selectedLocations = (state: RootState) => state.location.locations;
export const selectLocationLoading = (state: RootState) => state.location.loading;
export const selectLocationError = (state: RootState) => state.location.error;
export const selectLocationPagination = (state: RootState) => state.location.pagination

export default locationSlice.reducer;
